import { Package } from "lucide-react";
import AdminHeader from "../../component/AdminHeader";
import StatCard from "../../component/StatCard";
import { useEffect, useState } from "react";
import { http } from "../../axios/axios";
import Pagination from "../../component/Pagination";
import AdminResolutionModal from "../../component/AdminResolutionModal";
import { toast } from "react-toastify";
const AdminContact = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");
  const [allContacts, setAllContacts] = useState([]);
  const [pagination, setPagination] = useState({});
  const limit = 30;
  const [selectedContact, setSelectedContact] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);
  const [openMessageId, setOpenMessageId] = useState(null);
  const [count, setCount] = useState({
    pending: 0,
    resolved: 0,
  });

  const handleResolve = async (resolutionNote) => {
    if (!selectedContact) return;

    try {
      setResolvingId(selectedContact._id);
      const { data } = await http.patch(
        `contact/${selectedContact._id}/resolve`,
        {
          resolutionNote,
        },
      );

      setAllContacts((current) =>
        current.map((contact) =>
          contact._id === data.data._id ? data.data : contact,
        ),
      );
      setCount((current) => ({
        pending: Math.max(0, current.pending - 1),
        resolved: current.resolved + 1,
      }));
      setSelectedContact(null);
      toast.success(data.message);
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message ||
          "Unable to send email. Request was not marked as resolved.",
      );
    } finally {
      setResolvingId(null);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this contact?",
    );

    if (!confirmDelete) return;

    try {
      await http.delete(`contact/${id}`);

      if (openMessageId === id) {
        setOpenMessageId(null);
      }

      await fetchContacts();
    } catch (error) {
      console.error(error);
    }
  };

  const fetchContacts = async () => {
    try {
      const res = await http.get("contact", {
        params: {
          page,
          limit,
          status: status === "all" ? undefined : status,
        },
      });

      setAllContacts(res.data.data);
      setPagination(res.data.pagination);
      setCount(res.data.count);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [page, status]);

  return (
    <div className="p-4 md:p-8 min-h-screen text-slate-200 ">
      <AdminHeader title={"Manage Contacts"} />

      <div className="mb-6 flex items-center justify-end gap-3">
        <label className="text-sm text-slate-400">Status</label>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border-slate-700 bg-slate-900 px-4 py-2 text-white outline-none focus:border-blue-500"
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>
      <div className="flex flex-col md:flex-row w-full mb-8 gap-4">
        <StatCard
          title={"Total Submissions"}
          icon={Package}
          count={pagination.total}
        />
        <StatCard
          title={"Total Pending"}
          icon={Package}
          count={count?.pending}
        />
        <StatCard
          title={"Total Resolved"}
          icon={Package}
          count={count?.resolved}
        />
      </div>
      <div className="space-y-4">
        {allContacts?.length === 0 ? (
          <div className="p-8 rounded bg-white/5 border border-white/10 text-center text-slate-400">
            No Contact Found
          </div>
        ) : (
          allContacts.map((contact) => (
            <div
              key={contact?._id}
              className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 hover:border-slate-700 hover:bg-slate-900/60 transition"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1.5">
                  {contact?.name && (
                    <h3 className="text-lg font-semibold text-white">
                      {contact?.name}
                    </h3>
                  )}

                  {contact?.phone && (
                    <p className="text-sm text-slate-400">{contact?.phone}</p>
                  )}
                  {contact?.email && (
                    <p className="text-sm text-slate-400">{contact?.email}</p>
                  )}

                  {contact?.city && (
                    <p className="text-sm text-slate-400 ">
                      City: {contact?.city}
                    </p>
                  )}
                  {contact?.state && (
                    <p className="text-sm text-slate-400 ">
                      State: {contact?.state}
                    </p>
                  )}

                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    {contact.status === "pending" ? (
                      <button
                        onClick={() => setSelectedContact(contact)}
                        disabled={resolvingId === contact._id}
                        className="rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-green-600"
                      >
                        {resolvingId === contact._id
                          ? contact.email
                            ? "Sending..."
                            : "Resolving..."
                          : contact.email
                            ? "Resolve & Send Email"
                            : "Resolve Without Email"}
                      </button>
                    ) : (
                      <div>
                        <span className="rounded-md bg-green-700 px-3 py-1.5 text-sm font-medium text-white">
                          ✓ Resolved
                        </span>
                        <p className="mt-2 text-xs text-green-400">
                          {contact.resolutionEmailSent
                            ? "Resolution email sent"
                            : contact.email
                              ? "Resolved"
                              : "Resolved — No email available"}
                        </p>
                      </div>
                    )}
                    <button
                      onClick={() => handleDelete(contact?._id)}
                      className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <button
                  onClick={() =>
                    setOpenMessageId(
                      openMessageId === contact?._id ? null : contact?._id,
                    )
                  }
                  className="flex-shrink-0 p-2 text-slate-400 hover:text-white"
                >
                  <span
                    className={`inline-block transition-transform duration-300 ${openMessageId === contact?._id ? "rotate-180" : ""
                      }`}
                  >
                    ▼
                  </span>
                </button>
              </div>
              {openMessageId === contact?._id && (
                <div className="mt-4 w-full rounded-lg border border-slate-700 bg-slate-800 p-3 sm:p-4">
                  <p className="mb-1 text-xs text-slate-400">Message</p>
                  <p className="text-sm sm:text-base text-white whitespace-pre-wrap break-words">
                    {contact?.message}
                  </p>
                  {contact.resolvedAt && (
                    <p className="mt-4 text-xs text-slate-400">
                      Resolved: {new Date(contact.resolvedAt).toLocaleString()}
                    </p>
                  )}
                  {contact.resolutionEmailSentAt && (
                    <p className="mt-1 text-xs text-slate-400">
                      Email sent: {new Date(contact.resolutionEmailSentAt).toLocaleString()}
                    </p>
                  )}
                  {contact.resolutionNote && (
                    <div className="mt-4 border-t border-slate-700 pt-3">
                      <p className="text-xs text-slate-400">Resolution Note</p>
                      <p className="mt-1 whitespace-pre-wrap text-white">
                        {contact.resolutionNote}
                      </p>
                    </div>
                  )}
                </div>
              )}{" "}
            </div>
          ))
        )}
        {allContacts.length > 0 && (
          <Pagination
            page={page}
            totalPages={pagination?.totalPage}
            onPrevious={() => setPage((prev) => prev - 1)}
            onNext={() => setPage((prev) => prev + 1)}
          />
        )}
      </div>
      {selectedContact && (
        <AdminResolutionModal
          title="Resolve Contact Request"
          fields={[
            { label: "Customer Name", value: selectedContact.name },
            { label: "Customer Email", value: selectedContact.email || "No email available" },
            { label: "Phone", value: selectedContact.phone },
            { label: "City", value: selectedContact.city },
            { label: "State", value: selectedContact.state },
            { label: "Subject", value: selectedContact.subject },
            { label: "Preferred Contact Method", value: selectedContact.contactMethod },
            { label: "Customer Message", value: selectedContact.message, fullWidth: true },
          ]}
          noteLabel="Resolution / Response Note"
          notePlaceholder="Briefly describe how the request was resolved."
          hasEmail={Boolean(selectedContact.email)}
          sending={resolvingId === selectedContact._id}
          onClose={() => setSelectedContact(null)}
          onConfirm={handleResolve}
        />
      )}
    </div>
  );
};

export default AdminContact;
